import { useEffect, useRef } from "react";

/**
 * Re-runs `fn` every `intervalMs` and once on mount, plus immediately on
 * window focus / tab visibility regain (the common case: someone left the
 * tab, an invite/status changed elsewhere, they come back). Matches the
 * polling convention already used by NotificationBell (30s) — this exists
 * so other portal pages (invite status, order status, etc.) don't need
 * their own bespoke setInterval + focus-listener boilerplate, and so a
 * manual browser refresh is never required to see something change.
 *
 * `fn` is called via a ref so callers don't need to memoize it themselves.
 */
export default function usePolling(fn, intervalMs = 20000, enabled = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;
    fnRef.current();
    const interval = setInterval(() => fnRef.current(), intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") fnRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, enabled]);
}
